import React, { useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchFullJob } from "../../../redux/slices/fullJobFetchSlice"; // Import fetchFullJob
import { selectJobDetails, clearJobDetails } from "../../../redux/slices/jobDetailsSlice"; // Import selectJobDetails and clearJobDetails
import PrintGeneral from "../PrintJob/PrintGeneral"; // Import PrintGeneral
import PrintHeader from "../PrintJob/PrintHeader"; // Import PrintHeader
import PrintSales from "../PrintJob/PrintSales"; // Ensure correct import path
import { Modal, Button, Spinner } from "react-bootstrap"; // Import Modal, Button, and Spinner from react-bootstrap
import PrintPriceBreakdown from "./PrintPriceBreakdown";
import PrintSkid from "./PrintSkid";
import PrintEnclosure from "./PrintEnclosure";
import PrintHC from "./PrintHC";
import PrintPumpManifoldPiping from "./PrintPumpManifoldPiping";
import PrintDropPipes from "./PrintDropPipes";
import PrintLevelControls from "./PrintLevelControls";
import PrintFertigation from "./PrintFertigation";
import PrintSkyHarvester from "./PrintSkyHarvester";
import PrintShipLoose from "./PrintShipLoose";
import PrintWaterTreatment from "./PrintWaterTreatment";
import PrintPumps from "./PrintPumps";
import PrintPumpTable from "./PrintPumpTable";
import PrintElectricalOptions from "./PrintElectricalOptions";
import PrintUnregulated from "./PrintUnregulated";
import PrintRegulated from "./PrintRegulated";
import PrintTelemetry from "./PrintTelemetry";
import PrintSequenceOfOperations from "./PrintSequenceOfOperations";
import PrintControlLogic from "./PrintControlLogic"; // Import PrintControlLogic

const formatReportTimestamp = (date = new Date()) =>
  date.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const formatReportTitleTimestamp = (timestamp) =>
  timestamp.replace(/[^\dA-Za-z]+/g, "-").replace(/^-|-$/g, "");

const GenerateReport = () => {
  const dispatch = useDispatch();
  const jobId = useSelector((state) => state.jobId.jobId);
  const jobDetails = useSelector(selectJobDetails); // Get job details from Redux
  const { loading } = useSelector((state) => state.fullJobFetch); // Get loading state from fullJobFetch slice
  const modalPrintRef = useRef(); // Add ref for modal print section

  const [showModal, setShowModal] = useState(false); // State to control modal visibility
  const [reportDataReady, setReportDataReady] = useState(false); // Track if fresh data has been fetched for report
  const [reportGeneratedAt, setReportGeneratedAt] = useState("");

  // Helper function to check if a section has meaningful data
  // Ignores metadata fields and checks if any data field has a value
  const hasData = (sectionKey) => {
    const section = jobDetails[sectionKey];
    if (!section) return false;
    
    // Metadata fields to ignore when checking for data
    const metadataFields = [
      'ID', 'ProjectID', 'SaleQuoteID', 'Zone', 'HasTBD',
      'TelemetryParentID', 'PumpParentID', 'LevelControlParentID',
      'FillSourceParentID', 'BreakersParentID', 'PowerSupplyParentID',
      'DropPipeID', 'created_at', 'updated_at'
    ];
    
    // Check if a value is meaningful (not empty/null/undefined)
    const hasValue = (val) => {
      if (val === null || val === undefined || val === '') return false;
      if (typeof val === 'string' && val.trim() === '') return false;
      if (typeof val === 'number' && val === 0) return true; // 0 is a valid value
      return true;
    };
    
    // Check if an object has any meaningful data
    const objectHasData = (obj) => {
      return Object.entries(obj).some(([key, value]) => {
        // Skip metadata fields
        if (metadataFields.includes(key)) return false;
        // Check if this field has a meaningful value
        return hasValue(value);
      });
    };
    
    // Handle array of objects
    if (Array.isArray(section)) {
      if (section.length === 0) return false;
      // Check if at least one object has meaningful data
      return section.some(obj => objectHasData(obj));
    }
    
    // Handle single object
    return objectHasData(section);
  };
  const [includedCategories, setIncludedCategories] = useState({
    general: true,
    sales: true,
    mechanical: true,
    electrical: true,
  }); // State to control selected categories
  const [includedSalesSubCategories, setIncludedSalesSubCategories] = useState({
    soldToShipTo: true,
    priceBreakdown: true,
  }); // State to control selected sales subcategories
  const [includedMechanicalSubCategories, setIncludedMechanicalSubCategories] =
    useState({
      hcRequirements: true,
      skidConstruction: true,
      pumpManifoldPiping: true,
      stationEnclosure: true,
      dropPipes: true,
      pumps: true,
      pumpTable: true,
      skyHarvester: true,
      shipLoose: true,
    }); // State to control selected mechanical subcategories
  const [includedElectricalSubCategories, setIncludedElectricalSubCategories] =
    useState({
      options: true,
      power: true,
      telemetry: true,
      sequenceOfOperations: true,
      controlLogic: true, // Add controlLogic to includedElectricalSubCategories
    }); // State to control selected electrical subcategories
  const [includedSharedSubCategories, setIncludedSharedSubCategories] =
    useState({
      levelControls: true,
      waterTreatment: true,
      fertigation: true,
    }); // State to control shared subcategories

  const handleFetchDetails = async () => {
    // Clear any existing job data first to prevent stale data from showing
    dispatch(clearJobDetails());
    setReportDataReady(false);
    setReportGeneratedAt("");
    
    // Give Redux a moment to complete the clear operation
    await new Promise(resolve => setTimeout(resolve, 50));
    
    setShowModal(true); // Show modal with loading state
    
    // Fetch fresh job details specifically for this report
    try {
      await dispatch(fetchFullJob({ jobId })).unwrap();
      // Give Redux a moment to fully populate the store before rendering
      await new Promise(resolve => setTimeout(resolve, 100));
      setReportGeneratedAt(formatReportTimestamp());
      setReportDataReady(true); // Mark data as ready after successful fetch
    } catch (error) {
      console.error("Failed to fetch job details for report:", error);
      // Keep modal open so user can see the error state
    }
  };

  const handleCheckboxChange = (category) => {
    setIncludedCategories((prev) => {
      const newState = { ...prev, [category]: !prev[category] };
      if (category === "sales") {
        setIncludedSalesSubCategories({
          soldToShipTo: newState.sales,
          priceBreakdown: newState.sales,
        });
      }
      if (category === "mechanical") {
        setIncludedMechanicalSubCategories({
          hcRequirements: newState.mechanical,
          skidConstruction: newState.mechanical,
          pumpManifoldPiping: newState.mechanical,
          stationEnclosure: newState.mechanical,
          dropPipes: newState.mechanical,
          pumps: newState.mechanical,
          pumpTable: newState.mechanical,
          skyHarvester: newState.mechanical,
          shipLoose: newState.mechanical,
        });
        setIncludedSharedSubCategories({
          levelControls: newState.mechanical,
          waterTreatment: newState.mechanical,
          fertigation: newState.mechanical,
        });
      }
      if (category === "electrical") {
        setIncludedElectricalSubCategories({
          options: newState.electrical,
          power: newState.electrical,
          telemetry: newState.electrical,
          sequenceOfOperations: newState.electrical,
          controlLogic: newState.electrical, // Add controlLogic to includedElectricalSubCategories
        });
        setIncludedSharedSubCategories({
          levelControls: newState.electrical,
          waterTreatment: newState.electrical,
          fertigation: newState.electrical,
        });
      }
      return newState;
    });
  };

  const handleSalesSubCheckboxChange = (subCategory) => {
    setIncludedSalesSubCategories((prev) => ({
      ...prev,
      [subCategory]: !prev[subCategory],
    }));
  };

  const handleMechanicalSubCheckboxChange = (subCategory) => {
    setIncludedMechanicalSubCategories((prev) => ({
      ...prev,
      [subCategory]: !prev[subCategory],
    }));
  };

  const handleElectricalSubCheckboxChange = (subCategory) => {
    setIncludedElectricalSubCategories((prev) => ({
      ...prev,
      [subCategory]: !prev[subCategory],
    }));
  };

  const handleSharedSubCheckboxChange = (subCategory) => {
    setIncludedSharedSubCategories((prev) => ({
      ...prev,
      [subCategory]: !prev[subCategory],
    }));
  };

  const handleSelectAll = () => {
    setIncludedCategories({
      general: true,
      sales: true,
      mechanical: true,
      electrical: true,
    });
    setIncludedSalesSubCategories({
      soldToShipTo: true,
      priceBreakdown: true,
    });
    setIncludedMechanicalSubCategories({
      hcRequirements: true,
      skidConstruction: true,
      pumpManifoldPiping: true,
      stationEnclosure: true,
      dropPipes: true,
      pumps: true,
      pumpTable: true,
      skyHarvester: true,
      shipLoose: true,
    });
    setIncludedElectricalSubCategories({
      options: true,
      power: true,
      telemetry: true,
      sequenceOfOperations: true,
      controlLogic: true, // Add controlLogic to includedElectricalSubCategories
    });
    setIncludedSharedSubCategories({
      levelControls: true,
      waterTreatment: true,
      fertigation: true,
    });
  };

  const handleDeselectAll = () => {
    setIncludedCategories({
      general: false,
      sales: false,
      mechanical: false,
      electrical: false,
    });
    setIncludedSalesSubCategories({
      soldToShipTo: false,
      priceBreakdown: false,
    });
    setIncludedMechanicalSubCategories({
      hcRequirements: false,
      skidConstruction: false,
      pumpManifoldPiping: false,
      stationEnclosure: false,
      dropPipes: false,
      pumps: false,
      pumpTable: false,
      skyHarvester: false,
      shipLoose: false,
    });
    setIncludedElectricalSubCategories({
      options: false,
      power: false,
      telemetry: false,
      sequenceOfOperations: false,
      controlLogic: false, // Add controlLogic to includedElectricalSubCategories
    });
    setIncludedSharedSubCategories({
      levelControls: false,
      waterTreatment: false,
      fertigation: false,
    });
  };

  const handleModalPrint = () => {
    if (!modalPrintRef.current) return;

    // Open a new blank window
    const printWindow = window.open("", "", "height=800,width=1000");

    // Get the content to print
    const printContent = modalPrintRef.current.innerHTML;
    const titleTimestamp = reportGeneratedAt ? `-${formatReportTitleTimestamp(reportGeneratedAt)}` : "";

    // Inject styles from the current document
    const styles = Array.from(document.styleSheets)
      .map((styleSheet) => {
        try {
          return Array.from(styleSheet.cssRules)
            .map((rule) => rule.cssText)
            .join("\n");
        } catch (e) {
          console.warn("Cannot access stylesheet:", styleSheet.href);
          return "";
        }
      })
      .join("\n");

    // Write the content to the new print window
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Report${titleTimestamp}</title>
          <style>
            ${styles}
            @media print {
              .page-break { 
                page-break-before: always; 
              }
            }
          </style>  <!-- Inject styles directly -->
        </head>
        <body class="print-friendly">
          ${printContent}
        </body>
      </html>
    `);

    // Close document to ensure styles load before printing
    printWindow.document.close();

    // Wait a bit to ensure styles are applied before triggering print
    setTimeout(() => {
      printWindow.print();
      const closePrintWindow = () => printWindow.close();
      printWindow.onafterprint = closePrintWindow;
      printWindow.onfocus = () => {
        setTimeout(() => {
          if (printWindow.document.readyState === 'complete') {
            closePrintWindow();
          }
        }, 500);
      };
    }, 500);
  };





  return (
    <div>
      <div className="container">
        <h1 className="my-4">Generate Report</h1>
        {/* Category Selection Checkboxes */}
        <div className="mb-3">
          <h5>Select Categories to Include</h5>
          <div className="d-flex flex-wrap">
            <div className="form-check me-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="general"
                checked={includedCategories.general}
                onChange={() => handleCheckboxChange("general")}
              />
              <label className="form-check-label" htmlFor="general">
                General
              </label>
            </div>
            <div className="form-check me-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="sales"
                checked={includedCategories.sales}
                onChange={() => handleCheckboxChange("sales")}
              />
              <label className="form-check-label" htmlFor="sales">
                Sales Info
              </label>
              <div className="ms-4">
                <div className="form-check me-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="soldToShipTo"
                    checked={includedSalesSubCategories.soldToShipTo}
                    onChange={() =>
                      handleSalesSubCheckboxChange("soldToShipTo")
                    }
                  />
                  <label className="form-check-label" htmlFor="soldToShipTo">
                    Sold To/Ship To
                  </label>
                </div>
                <div className="form-check me-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="priceBreakdown"
                    checked={includedSalesSubCategories.priceBreakdown}
                    onChange={() =>
                      handleSalesSubCheckboxChange("priceBreakdown")
                    }
                  />
                  <label className="form-check-label" htmlFor="priceBreakdown">
                    Price Breakdown
                  </label>
                </div>
              </div>
            </div>
            <div className="form-check me-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="mechanical"
                checked={includedCategories.mechanical}
                onChange={() => handleCheckboxChange("mechanical")}
              />
              <label className="form-check-label" htmlFor="mechanical">
                Mechanical
              </label>
              <div className="ms-4">
                <div className="form-check me-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="hcRequirements"
                    checked={includedMechanicalSubCategories.hcRequirements}
                    onChange={() =>
                      handleMechanicalSubCheckboxChange("hcRequirements")
                    }
                  />
                  <label className="form-check-label" htmlFor="hcRequirements">
                    HC Requirements
                  </label>
                </div>
                <div className="form-check me-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="skidConstruction"
                    checked={includedMechanicalSubCategories.skidConstruction}
                    onChange={() =>
                      handleMechanicalSubCheckboxChange("skidConstruction")
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor="skidConstruction"
                  >
                    Skid Construction
                  </label>
                </div>
                <div className="form-check me-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="pumpManifoldPiping"
                    checked={includedMechanicalSubCategories.pumpManifoldPiping}
                    onChange={() =>
                      handleMechanicalSubCheckboxChange("pumpManifoldPiping")
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor="pumpManifoldPiping"
                  >
                    Pump/Manifold/Piping
                  </label>
                </div>
                <div className="form-check me-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="stationEnclosure"
                    checked={includedMechanicalSubCategories.stationEnclosure}
                    onChange={() =>
                      handleMechanicalSubCheckboxChange("stationEnclosure")
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor="stationEnclosure"
                  >
                    Station Enclosure
                  </label>
                </div>
                <div className="form-check me-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="dropPipes"
                    checked={includedMechanicalSubCategories.dropPipes}
                    onChange={() =>
                      handleMechanicalSubCheckboxChange("dropPipes")
                    }
                  />
                  <label className="form-check-label" htmlFor="dropPipes">
                    Drop Pipes
                  </label>
                </div>
                <div className="form-check me-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="pumps"
                    checked={includedMechanicalSubCategories.pumps}
                    onChange={() => handleMechanicalSubCheckboxChange("pumps")}
                  />
                  <label className="form-check-label" htmlFor="pumps">
                    Pumps
                  </label>
                </div>
                <div className="form-check me-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="pumpTable"
                    checked={includedMechanicalSubCategories.pumpTable}
                    onChange={() =>
                      handleMechanicalSubCheckboxChange("pumpTable")
                    }
                  />
                  <label className="form-check-label" htmlFor="pumpTable">
                    Pump Table
                  </label>
                </div>
                <div className="form-check me-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="skyHarvester"
                    checked={includedMechanicalSubCategories.skyHarvester}
                    onChange={() =>
                      handleMechanicalSubCheckboxChange("skyHarvester")
                    }
                  />
                  <label className="form-check-label" htmlFor="skyHarvester">
                    Sky Harvester
                  </label>
                </div>
                <div className="form-check me-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="shipLoose"
                    checked={includedMechanicalSubCategories.shipLoose}
                    onChange={() =>
                      handleMechanicalSubCheckboxChange("shipLoose")
                    }
                  />
                  <label className="form-check-label" htmlFor="shipLoose">
                    Ship Loose
                  </label>
                </div>
              </div>
            </div>
            <div className="form-check me-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="electrical"
                checked={includedCategories.electrical}
                onChange={() => handleCheckboxChange("electrical")}
              />
              <label className="form-check-label" htmlFor="electrical">
                Electrical
              </label>
              <div className="ms-4">
                <div className="form-check me-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="options"
                    checked={includedElectricalSubCategories.options}
                    onChange={() =>
                      handleElectricalSubCheckboxChange("options")
                    }
                  />
                  <label className="form-check-label" htmlFor="options">
                    Options
                  </label>
                </div>
                <div className="form-check me-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="power"
                    checked={includedElectricalSubCategories.power}
                    onChange={() => handleElectricalSubCheckboxChange("power")}
                  />
                  <label className="form-check-label" htmlFor="power">
                    Unregulated/Regulated Power
                  </label>
                </div>
                <div className="form-check me-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="telemetry"
                    checked={includedElectricalSubCategories.telemetry}
                    onChange={() =>
                      handleElectricalSubCheckboxChange("telemetry")
                    }
                  />
                  <label className="form-check-label" htmlFor="telemetry">
                    Telemetry
                  </label>
                </div>
                <div className="form-check me-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="sequenceOfOperations"
                    checked={
                      includedElectricalSubCategories.sequenceOfOperations
                    }
                    onChange={() =>
                      handleElectricalSubCheckboxChange("sequenceOfOperations")
                    }
                  />
                  <label
                    className="form-check-label"
                    htmlFor="sequenceOfOperations"
                  >
                    Sequence of Operations
                  </label>
                </div>
                <div className="form-check me-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="controlLogic"
                    checked={includedElectricalSubCategories.controlLogic}
                    onChange={() =>
                      handleElectricalSubCheckboxChange("controlLogic")
                    }
                  />
                  <label className="form-check-label" htmlFor="controlLogic">
                    Control Logic
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex flex-wrap mt-3">
            <div className="form-check me-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="levelControls"
                checked={includedSharedSubCategories.levelControls}
                onChange={() => handleSharedSubCheckboxChange("levelControls")}
              />
              <label className="form-check-label" htmlFor="levelControls">
                Level Controls
              </label>
            </div>
            <div className="form-check me-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="waterTreatment"
                checked={includedSharedSubCategories.waterTreatment}
                onChange={() => handleSharedSubCheckboxChange("waterTreatment")}
              />
              <label className="form-check-label" htmlFor="waterTreatment">
                Water Treatment
              </label>
            </div>
            <div className="form-check me-3">
              <input
                type="checkbox"
                className="form-check-input"
                id="fertigation"
                checked={includedSharedSubCategories.fertigation}
                onChange={() => handleSharedSubCheckboxChange("fertigation")}
              />
              <label className="form-check-label" htmlFor="fertigation">
                Fertigation
              </label>
            </div>
          </div>
          <div className="mt-2">
            <button className="btn btn-primary me-2" onClick={handleSelectAll}>
              Select All
            </button>
            <button className="btn btn-secondary" onClick={handleDeselectAll}>
              Deselect All
            </button>
          </div>
        </div>

        {/* Fetch Details Button */}
        <div className="input-group mb-3">
          <button className="btn btn-primary" onClick={handleFetchDetails}>
            Fetch Job Details
          </button>
        </div>

        {/* Modal */}
        <Modal 
          show={showModal} 
          onHide={() => {
            setShowModal(false);
            setReportDataReady(false); // Reset data ready flag when closing modal
            setReportGeneratedAt("");
          }} 
          size="xl"
        >
          <Modal.Header closeButton>
            <Modal.Title>Print Preview</Modal.Title>
          </Modal.Header>
          <Modal.Body ref={modalPrintRef}>
            {loading || !reportDataReady ? (
              <div className="text-center">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-2">Fetching fresh job data for report...</p>
              </div>
            ) : (
              <div key={`report-content-${jobId}-${reportDataReady}`}>
                <div className="print-header">
                  <PrintHeader generatedAt={reportGeneratedAt} /> {/* Display PrintHeader */}
                </div>
                {jobDetails && (
                  <>
                    {includedCategories.general && (hasData('generalProject') || hasData('generalStation')) && (
                      <PrintGeneral jobDetails={jobDetails} />
                    )}

                    {includedSalesSubCategories.soldToShipTo && hasData('soldToShipTo') && (
                      <PrintSales jobDetails={jobDetails} />
                    )}
                    {includedSalesSubCategories.priceBreakdown && (hasData('priceBreakdown') || hasData('ownersManual')) && (
                      <PrintPriceBreakdown jobDetails={jobDetails} />
                    )}

                    {includedMechanicalSubCategories.hcRequirements && hasData('hcRequirements') && (
                      <PrintHC jobDetails={jobDetails} />
                    )}
                    {includedMechanicalSubCategories.skidConstruction && hasData('skidConstruction') && (
                      <PrintSkid jobDetails={jobDetails} />
                    )}
                    {includedMechanicalSubCategories.pumpManifoldPiping && hasData('pumpManifoldPiping') && (
                      <PrintPumpManifoldPiping jobDetails={jobDetails} />
                    )}
                    {includedMechanicalSubCategories.stationEnclosure && hasData('stationEnclosure') && (
                      <PrintEnclosure jobDetails={jobDetails} />
                    )}
                    {includedMechanicalSubCategories.dropPipes && (hasData('dropPipeComments') || hasData('dropPipes')) && (
                      <PrintDropPipes jobDetails={jobDetails} />
                    )}
                    {includedMechanicalSubCategories.pumps && hasData('pumps') && (
                      <PrintPumps jobDetails={jobDetails} />
                    )}
                    {includedMechanicalSubCategories.pumpTable && hasData('pumpDetails') && (
                      <PrintPumpTable jobDetails={jobDetails} />
                    )}
                    {includedMechanicalSubCategories.skyHarvester && hasData('skyHarvester') && (
                      <PrintSkyHarvester jobDetails={jobDetails} />
                    )}
                    {includedMechanicalSubCategories.shipLoose && hasData('shipLoose') && (
                      <PrintShipLoose jobDetails={jobDetails} />
                    )}
                    {includedElectricalSubCategories.power && (hasData('unregulatedPower') || hasData('regulatedPower')) && (
                      <>
                        {hasData('unregulatedPower') && <PrintUnregulated jobDetails={jobDetails} />}
                        {hasData('regulatedPower') && <PrintRegulated jobDetails={jobDetails} />}
                      </>
                    )}
                    {includedElectricalSubCategories.telemetry && hasData('telemetry') && (
                      <PrintTelemetry jobDetails={jobDetails} />
                    )}
                    {includedElectricalSubCategories.sequenceOfOperations && hasData('sequenceOfOperations') && (
                      <PrintSequenceOfOperations jobDetails={jobDetails} />
                    )}
                    {includedElectricalSubCategories.controlLogic && hasData('controlLogicOnly') && (
                      <PrintControlLogic jobDetails={jobDetails} />
                    )}
                    {includedElectricalSubCategories.options && hasData('electricalOptions') && (
                      <PrintElectricalOptions jobDetails={jobDetails} />
                    )}
                    {includedSharedSubCategories.levelControls && hasData('levelControls') && (
                      <PrintLevelControls jobDetails={jobDetails} />
                    )}
                    {includedSharedSubCategories.waterTreatment && hasData('waterTreatment') && (
                      <PrintWaterTreatment jobDetails={jobDetails} />
                    )}
                    {includedSharedSubCategories.fertigation && hasData('fertigation') && (
                      <PrintFertigation jobDetails={jobDetails} />
                    )}
                  </>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowModal(false);
              setReportDataReady(false); // Reset data ready flag when closing modal
              setReportGeneratedAt("");
            }}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleModalPrint}
              disabled={loading || !reportDataReady}
            >
              Print
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default GenerateReport;
